import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import JsonModels from '@/ts/models.json';
import { usePermissions } from '@/composables/usePermissions';

// Models that are searchable in models.json but intentionally get no list
// page of their own. LocationZonePlayerParsedPlaylistTrack is a join row
// between a player and the tracks of the playlist it parsed. You reach those
// through the player, never by searching for one.
// LocationZoneCustomPlaylistSchedule is searched through its custom list: the
// custom list search has a "Schedule" filter group, and each card shows the
// schedules that pass. The schedule search endpoint stays, because bulk edit uses it.
export const PICKER_HIDDEN_ENTITY_IDS: ReadonlySet<string> = new Set([
    'Country',
    'Template',
    'LocationZonePlayerParsedPlaylistTrack',
    'LocationZoneCustomPlaylistSchedule',
]);

/**
 * models.json entityId → permissions model key: the snake_case of the
 * entityId, NEVER models.json `_slug`. Two entities' slugs diverge from their
 * policy keys (ContactLoginCustomPlaylist's slug is contact_custom_playlist,
 * LocationZoneCustomPlaylistSchedule's is custom_playlist_schedule); the
 * snake_case transform matches every backend BaseModelPolicy::modelKey(),
 * pinned there by SearchRouteAuthorizationTest's derivation test (SAU-287).
 *
 * The regex mirrors Laravel's Str::snake EXACTLY — a delimiter before every
 * uppercase letter, not only after a lowercase one. The looser
 * `([a-z0-9])([A-Z])` form agrees on today's entities but diverges the moment
 * a name carries consecutive capitals: Str::snake('LocationZoneRFIDTag') is
 * `location_zone_r_f_i_d_tag`, while the looser form yields
 * `location_zone_rfidtag`. That mismatch would hide the entity from every
 * non-developer with nothing failing anywhere.
 */
export function permissionKeyFor(entityId: string): string {
    return entityId.replace(/(.)(?=[A-Z])/g, '$1_').toLowerCase();
}

/**
 * Entities the picker draws as a sub-row of another entity, with a short label.
 * None do today. The schedule was the one sub-row, drawn as "Schedule" under
 * "Custom Playlist", until it was hidden from the picker.
 */
const ENTITY_PARENTS: Readonly<Record<string, { parentEntityId: string; pickerLabel: string }>> = {};

export type PickerEntity = {
    entityId: string;
    entityDisplayName: string;
    entitySlug: string;
    /** The entity this one is listed under in the picker, or null for a top-level entry. */
    parentEntityId: string | null;
    /** The label the picker shows: the short label for a sub-row, otherwise the display name. */
    pickerLabel: string;
};

const displayNames = new Map(
    Object.entries(JsonModels)
        .filter(([, modelProperties]) => '_displayName' in modelProperties)
        .map(([key, modelProperties]: [string, any]) => [key, modelProperties._displayName.slice(1, -1) as string]),
);

/** A sub-row sorts straight after its parent. Sub-rows of one parent sort by their label. */
function comparePickerEntities(a: PickerEntity, b: PickerEntity): number {
    const groupOf = (entity: PickerEntity) =>
        entity.parentEntityId === null ? entity.entityDisplayName : (displayNames.get(entity.parentEntityId) ?? entity.entityDisplayName);
    const byGroup = groupOf(a).localeCompare(groupOf(b));
    if (byGroup !== 0) return byGroup;
    if ((a.parentEntityId === null) !== (b.parentEntityId === null)) return a.parentEntityId === null ? -1 : 1;
    return a.pickerLabel.localeCompare(b.pickerLabel);
}

const ALL_ENTITIES: PickerEntity[] = Object.entries(JsonModels)
    .filter(([key, modelProperties]) => '_displayName' in modelProperties && !PICKER_HIDDEN_ENTITY_IDS.has(key))
    .map(([key, modelProperties]: [string, any]) => {
        const entityDisplayName = modelProperties._displayName.slice(1, -1);
        const parent = ENTITY_PARENTS[key];
        return {
            entityId: key,
            entityDisplayName,
            entitySlug: modelProperties._slug.slice(1, -1),
            parentEntityId: parent?.parentEntityId ?? null,
            pickerLabel: parent?.pickerLabel ?? entityDisplayName,
        };
    })
    .sort(comparePickerEntities);

export const useEntityStore = defineStore('entityStore', () => {
    const { can } = usePermissions();

    // Permission-filtered: a user sees exactly the models they hold can_view
    // for (developers pass everything via the can() bypass). The backend
    // enforces the same rule with viewAny middleware on every search route —
    // this filter is UX, not security (SAU-287).
    // A sub-row whose parent this user cannot see would sit under nothing, so it
    // is listed as a top-level entry with its full name instead.
    const entities = computed<PickerEntity[]>(() => {
        const permitted = ALL_ENTITIES.filter((entity) => can('view', permissionKeyFor(entity.entityId)));
        const permittedIds = new Set(permitted.map((entity) => entity.entityId));
        return permitted.map((entity) =>
            entity.parentEntityId === null || permittedIds.has(entity.parentEntityId)
                ? entity
                : { ...entity, parentEntityId: null, pickerLabel: entity.entityDisplayName },
        );
    });

    // May start empty: permissions arrive with /api/user after store setup,
    // and a zero-grant user never gets a list. Never index [0] unguarded.
    const selectedEntityId = ref<string>(entities.value[0]?.entityId ?? '');

    // The persisted selection can reference an entity that no longer exists in
    // models.json (e.g. Player after the consolidation into LocationZonePlayer)
    // or one this user cannot view; fall back to the first permitted entity.
    const selectedEntity = computed(() =>
        entities.value.find((e) => e.entityId === selectedEntityId.value) || entities.value[0] || null
    );

    // Persisted-state hydration happens after setup, so also self-heal the
    // stored id — otherwise the entity selector keeps showing a placeholder
    // for a removed/forbidden entity. Only heal when the list is non-empty:
    // resetting while permissions are still loading would clobber a valid
    // persisted selection.
    watch([selectedEntityId, entities], ([entityId, permittedEntities]) => {
        if (permittedEntities.length === 0) {
            return;
        }
        if (!permittedEntities.some((e) => e.entityId === entityId)) {
            selectedEntityId.value = permittedEntities[0].entityId;
        }
    });

    const entitySlug = computed(() => selectedEntity.value?.entitySlug || '');

    function setSelectedEntity(entityId: string) {
        selectedEntityId.value = entityId;
    }

    return {
        selectedEntityId,
        entities,
        selectedEntity,
        entitySlug,
        setSelectedEntity,
    };
}, {
    persist: {
        key: 'entityStore',
        pick: ['selectedEntityId']
    }
});
